import React from 'react';
import moment from 'moment';
import { canEncodeCode128B } from './code128';
import { Code128Barcode } from './Code128Barcode';
import type { KitSheetPatient } from './kitSheetPatient';

// The sheet is printed from an iframe that doesn't share the app's
// stylesheets, so every style is inline.
export const MONOSPACE_FONT = 'ui-monospace, Menlo, Consolas, monospace';
const BARCODE_MAX_WIDTH_PX = 640;
const EMPTY_VALUE = '—';

export const SPECIMEN = 'Blood, drawn on site';

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: 48,
    color: '#000',
    background: '#fff',
    fontFamily: 'Helvetica, Arial, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '2px solid #000',
    paddingBottom: 12,
  },
  title: { fontSize: 22, fontWeight: 700 },
  instructions: { fontSize: 13 },
  printedAt: { fontSize: 11, textAlign: 'right' },
  barcodeArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '32px 24px',
    border: '1px solid #000',
  },
  rqId: {
    fontFamily: MONOSPACE_FONT,
    fontSize: 28,
    letterSpacing: 2,
    overflowWrap: 'anywhere',
    textAlign: 'center',
  },
  rqIdCaption: { fontSize: 12 },
  patientTable: { fontSize: 15, borderCollapse: 'collapse', width: '100%' },
  patientRow: { borderBottom: '1px solid #ccc' },
  patientLabel: { padding: '8px 0', width: 200, fontWeight: 600 },
  patientValue: { padding: '8px 0' },
  handwriteRow: { display: 'flex', gap: 24 },
  handwriteLine: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    flexBasis: 0,
    minWidth: 0,
  },
  handwriteLabel: { whiteSpace: 'nowrap', fontSize: 12 },
  handwriteBlank: { flex: 1, borderBottom: '1px solid #000', height: 18 },
};

export type HandwriteLineProps = {
  label: string;
  /** Width relative to the other lines in the same row. Defaults to 1. */
  flexWeight?: number;
};

/** A label followed by a blank rule to write on. */
export const HandwriteLine = ({
  label,
  flexWeight = 1,
}: HandwriteLineProps) => (
  <div style={{ ...styles.handwriteLine, flexGrow: flexWeight }}>
    <span style={styles.handwriteLabel}>{label}</span>
    <span style={styles.handwriteBlank} />
  </div>
);

export type RqIdBarcodeAreaProps = {
  rqId: string;
};

/**
 * The hero barcode: Code 128 of the bare RQ id, with the id under it. An id
 * that Code 128 set B can't encode prints as text only.
 */
export const RqIdBarcodeArea = ({ rqId }: RqIdBarcodeAreaProps) => (
  <div style={styles.barcodeArea}>
    {canEncodeCode128B(rqId) ? (
      <Code128Barcode
        value={rqId}
        heightPx={96}
        maxWidthPx={BARCODE_MAX_WIDTH_PX}
      />
    ) : null}
    <div style={styles.rqId}>{rqId}</div>
    <div style={styles.rqIdCaption}>MyOme requisition id</div>
  </div>
);

export type MyOmeKitSheetProps = {
  patient: KitSheetPatient;
  printedAt: Date;
  /**
   * The block between the header and the patient table: `RqIdBarcodeArea`.
   */
  requisitionArea: React.ReactNode;
};

/**
 * A letter-size sheet that goes in the box with blood drawn on site, so
 * MyOme can match the specimen to its requisition.
 */
export const MyOmeKitSheet = ({
  patient,
  printedAt,
  requisitionArea,
}: MyOmeKitSheetProps) => {
  const rows = [
    { label: 'Patient', value: patient.name },
    { label: 'Date of birth', value: patient.birthDate },
    { label: 'Elation patient id', value: patient.elationPatientId },
    { label: 'Specimen', value: SPECIMEN },
  ];
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>MyOme specimen requisition</div>
          <div style={styles.instructions}>
            Place this sheet in the specimen box with the blood tubes.
          </div>
        </div>
        <div style={styles.printedAt}>
          Fountain Life
          <br />
          Printed {moment(printedAt).format('M/D/YYYY, h:mm A')}
        </div>
      </div>

      {requisitionArea}

      <table style={styles.patientTable}>
        <tbody>
          {rows.map(({ label, value }) => (
            <tr key={label} style={styles.patientRow}>
              <th scope="row" style={styles.patientLabel} align="left">
                {label}
              </th>
              <td style={styles.patientValue}>{value || EMPTY_VALUE}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={styles.handwriteRow}>
        <HandwriteLine label="Collected on" flexWeight={2} />
        <HandwriteLine label="Time" flexWeight={0.8} />
        <HandwriteLine label="Collector initials" />
      </div>
    </div>
  );
};
